import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, FormRenderProps } from 'react-final-form';
import { Stack, Button, CircularProgress, Card, CardContent, Typography } from '@mui/material';

import { Page, useAppLayoutContextConsumer, snackbar } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { useUpdateKioskAssetMutation } from '@occc/graphql';

import { AssetEditForm } from './components/asset-edit-form.component';
import { validate } from './assets-edit.validation';
import { AssetEditFormValues } from './assets-edit.types';

import { routeCreator } from '../../touchpoint.router';
import { useGetKioskAsset } from '../../hooks/use-get-kiosk-asset.hook';
import { SyncInfoDialog } from '../../components/sync-info-dialog.component';
import {
  ALL_IMAGES_REQUIRED_MESSAGE,
  ASSET_UPDATE_SUCCESS_MESSAGE,
  ASSET_UPDATE_ERROR_MESSAGE,
  ASSET_CONFIRMATION_MESSAGE,
} from '../../touchpoint.constants';

const PAGE_TITLE = 'Edit asset';

export const AssetsEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { loading, error, asset } = useGetKioskAsset({ id: id || '' });
  const [updateKioskAsset] = useUpdateKioskAssetMutation();

  const initialValues: Partial<AssetEditFormValues> = useMemo(() => {
    if (!asset) {
      return {
        name: '',
        description: '',
        landscapeImage: null,
        squareImage: null,
        portraitImage: null,
        landscapeImageUrl: null,
        squareImageUrl: null,
        portraitImageUrl: null,
      };
    }

    return {
      name: asset.name || '',
      description: asset.description || '',
      landscapeImage: null,
      squareImage: null,
      portraitImage: null,
      landscapeImageUrl: asset.imageSet?.landscape || null,
      squareImageUrl: asset.imageSet?.square || null,
      portraitImageUrl: asset.imageSet?.portrait || null,
    };
  }, [asset]);

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.assetsList(),
        label: 'Touchpoint',
      },
      {
        key: routeCreator.assetsList(),
        label: 'Assets',
      },
    ],
    [],
  );

  useAppLayoutContextConsumer(breadcrumbLinks);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback(
    async (values: AssetEditFormValues) => {
      if (!id) return;

      setIsSubmitting(true);

      try {
        if (!values.squareImageUrl || !values.landscapeImageUrl || !values.portraitImageUrl) {
          snackbar.error(ALL_IMAGES_REQUIRED_MESSAGE);
          setIsSubmitting(false);
          return;
        }

        // Build imageSet object with all image URLs
        const imageSet = {
          landscape: values.landscapeImageUrl,
          square: values.squareImageUrl,
          portrait: values.portraitImageUrl,
        };

        const { data } = await updateKioskAsset({
          variables: {
            input: {
              id,
              name: values.name.trim(),
              description: values.description.trim() || undefined,
              imageSet,
            },
          },
          refetchQueries: ['kioskAssets', 'kioskAsset'],
        });

        if (data?.updateKioskAsset) {
          snackbar.success(ASSET_UPDATE_SUCCESS_MESSAGE);
          navigate(routeCreator.assetsList());
        } else {
          snackbar.error(ASSET_UPDATE_ERROR_MESSAGE);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error updating asset:', error);
        snackbar.error(ASSET_UPDATE_ERROR_MESSAGE);
        setIsSubmitting(false);
      }
    },
    [updateKioskAsset, id, navigate],
  );

  const renderForm = ({
    handleSubmit,
    valid,
    submitting,
  }: FormRenderProps<AssetEditFormValues>) => {
    const isDisabled = submitting || isSubmitting || !valid;

    const handleConfirmSave = () => {
      setShowConfirmDialog(false);
      handleSubmit();
    };

    return (
      <>
        <Page
          browserTabTitle={PAGE_TITLE}
          pageHeaderProps={{
            title: PAGE_TITLE,
            subtitle:
              'Edit the image asset for your in-store ads. Update the visual and your relevant details.',
          }}
          actions={
            <Stack direction='row' spacing={1}>
              <Button variant='outlined' color='secondary' onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant='contained'
                color='secondary'
                onClick={() => setShowConfirmDialog(true)}
                disabled={isDisabled}
              >
                Save
              </Button>
            </Stack>
          }
        >
          <AssetEditForm handleSubmit={handleSubmit} />
        </Page>
        <SyncInfoDialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirmSave}
          message={ASSET_CONFIRMATION_MESSAGE}
        />
      </>
    );
  };

  // Handle missing ID case
  if (!id) {
    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: 'Error',
        }}
      >
        <Card>
          <CardContent>
            <Typography color='error'>Asset ID is required</Typography>
            <Button onClick={() => navigate(routeCreator.assetsList())} sx={{ mt: 2 }}>
              Back to Assets
            </Button>
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: 'Loading...',
        }}
      >
        <Card>
          <CardContent>
            <CircularProgress />
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (error || !asset) {
    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: 'Error',
        }}
      >
        <Card>
          <CardContent>
            <Typography color='error'>{error?.message || 'Asset not found'}</Typography>
            <Button onClick={handleCancel} sx={{ mt: 2 }}>
              Back
            </Button>
          </CardContent>
        </Card>
      </Page>
    );
  }

  return (
    <Form<AssetEditFormValues>
      onSubmit={onSubmit}
      render={renderForm}
      validate={validate}
      initialValues={initialValues}
      keepDirtyOnReinitialize
    />
  );
};
